import { Icon } from "@iconify/react";
import { format } from "date-fns";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import AlertDialog from "../../components/shared/AlertDialog";
import { Input, Label } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import {
  useCreatePlanDetails,
  useGetAllPlanDocs,
  usePlanStateChange,
} from "../../queries";
import { useCompanyStore } from "../../store/companyStore";
import { useError } from "../../store/errorStore";
import { EsopPlan, PlanState } from "../../types/EsopPlan";
import { PlanStateChangeDto } from "../../types/Grant";
import { getCurrencyType } from "../../utils/currencyFormatter";
import { formatDate } from "../../utils/date";
import { getFormattedValue, stringFormat } from "../../utils/string";
import StatusLabel from "./StatusLabel";
import { useGetEmployeePortalSettings } from "../../queries/siteSettings";
import InfoBox from "../../components/shared/InfoBox";

function PlanStateChange({
  plan,
  onClose = () => {},
}: {
  plan: EsopPlan;
  onClose: () => void;
}) {
  const [showDialog, setShowDialog] = useState(false);
  const { mutate: changePlanState, status } = usePlanStateChange();
  const errorMessage = useError();
  const { data: planDocuments } = useGetAllPlanDocs(plan.esopPlanId || "");
  const { data: settings } = useGetEmployeePortalSettings();
  const initialValues: PlanStateChangeDto = {
    esopPlanState: plan.availableState[0],
    note: plan.note,
    approvalDate: "",
    esopPlanId: plan.esopPlanId,
    planPoolSize: plan.poolSize,
    planStartDate: (plan.planStartDate && formatDate(plan.planStartDate)) || "",
  };
  const areAnyPlanDocumentsAvailable = useMemo(
    () =>
      Array.isArray(planDocuments) &&
      planDocuments.length > 0 &&
      planDocuments.some((doc) => doc.metaData?.shownToEmployee),
    [planDocuments]
  );

  const _availableStates = plan.availableState || [];
  const availablestates = _availableStates.map((state) =>
    stringFormat(state).toLowerCase()
  );
  const { companyData } = useCompanyStore();
  const { data: createPlanDetails } = useCreatePlanDetails();
  const minStartDate = new Date(
    companyData?.companyMetaData.dateOfIncorporation || "1900-01-01"
  );
  const validationSchema = Yup.object().shape({
    planStartDate: Yup.date()
      .required("Plan Start Date is required")
      .min(
        minStartDate,
        `Plan date should not be before company Incorporation date (
    ${format(
      new Date(
        companyData?.companyMetaData.dateOfIncorporation || "1900-01-01"
      ),
      "dd-MM-yyyy"
    )})`
      ),
    approvalDate: Yup.date().when("esopPlanState", {
      is: (val: string) => val?.toUpperCase() === PlanState.ACTIVE,
      then: (schema) => schema.required("Approval Date is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    note: Yup.string().required("Note is required"),
    planPoolSize: Yup.number()
      .required("Pool Size is required")
      .positive("Pool Size must be a positive number")
      .test(
        "min-pool-size",
        `Pool Size must be at least ${getFormattedValue(
          plan.outstandingAllocatedPool,
          getCurrencyType()
        )} options to cover allocations under the plan`,
        (value, context) => {
          const parent = context.parent as PlanStateChangeDto;
          if (parent.esopPlanState?.toUpperCase() === "AMENDMENT REJECTED") {
            return true;
          }
          return (value || 0) >= plan.outstandingAllocatedPool;
        }
      )
      .test(
        "Pool Size",
        "Pool Size must be lesser than available options ",
        (_value, context) => {
          const value = context.parent as PlanStateChangeDto;
          const optionsAvailable =
            plan?.esopPlanState === PlanState.ACTIVE ||
            plan?.esopPlanState === PlanState.AMENDMENT
              ? (createPlanDetails?.esopPoolCount || 0) +
                (plan?.poolSize || 0) * (plan?.conversionNumber || 1)
              : createPlanDetails?.esopPoolCount || 0;
          const sharesAvailable = optionsAvailable * plan.conversionNumber;
          if (value.planPoolSize > sharesAvailable) {
            return false;
          }
          return true;
        }
      )
      .test(
        "is-fractional",
        "Pool Size must be an integer",
        (_value, context) => {
          const value = context.parent as PlanStateChangeDto;
          if (plan?.isFractional) {
            return true;
          } else {
            return Number.isInteger(value?.planPoolSize);
          }
        }
      ),
  });
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      handleSubmit();
    },
  });
  function handleSubmit() {
    if (!formik.isValid) {
      formik.setTouched({
        approvalDate: true,
        planStartDate: true,
        planPoolSize: true,
        note: true,
      });
      return;
    }
    const formattedPlanState = formik.values.esopPlanState
      .toUpperCase()
      .replace(/ /g, "_");
    if (!plan.esopPlanId || !formattedPlanState) return;
    const submissionValues = {
      ...formik.values,
      esopPlanState: formattedPlanState,
    };
    changePlanState(submissionValues, {
      onSuccess: () => {
        toast("State Changed Successfully!", { type: "success" });
        onClose();
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response.data.reason);
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      },
    });
  }
  const sharesAvailable =
    plan?.esopPlanState === PlanState.ACTIVE ||
    plan?.esopPlanState === PlanState.AMENDMENT
      ? (createPlanDetails?.esopPoolCount || 0) +
        (plan?.poolSize || 0) * plan.conversionNumber
      : createPlanDetails?.esopPoolCount || 0;
  const currency = getCurrencyType();
  const minimumPoolSizeRequired = plan.outstandingAllocatedPool;
  return (
    <>
      <div className="p-4 text-lg font-medium border-b">
        <h6 className="flex justify-between">
          Change Plan Status{" "}
          <span onClick={() => onClose()} className="cursor-pointer">
            <Icon icon="material-symbols:close-rounded" />
          </span>
        </h6>
      </div>
      <VStack className="justify-between p-10 min-w-[700px] gap-8">
        <HStack className="justify-between">
          <p className="font-semibold">{plan.planName}</p>
          <StatusLabel state={plan.esopPlanState ?? ""} />
        </HStack>
        <HStack>
          <Box className="w-1/2 pr-3">
            <Label className="text-sm font-normal">Select Plan Status</Label>
            <Select
              className="capitalize"
              options={availablestates}
              isPlaceHolderDisabled={true}
              onChange={(e) =>
                formik.setFieldValue("esopPlanState", e.target.value)
              }
            />
          </Box>
          {formik.values.esopPlanState.toUpperCase() === PlanState.ACTIVE && (
            <Box className="w-1/2 pr-3">
              <Label className="required text-sm font-normal">
                Approval Date
              </Label>
              <Input type="date" {...formik.getFieldProps("approvalDate")} />
              {formik.touched.approvalDate && formik.errors.approvalDate && (
                <Error text={formik.errors.approvalDate} />
              )}
            </Box>
          )}
        </HStack>
        <HStack>
          <Box className="w-1/2 pr-3">
            <Label className="required text-sm font-normal">
              Plan Start Date
            </Label>
            <Input type="date" {...formik.getFieldProps("planStartDate")} />
            {formik.touched.planStartDate && formik.errors.planStartDate && (
              <Error text={formik.errors.planStartDate} />
            )}
          </Box>
          <Box className="w-1/2 px-3">
            <div className="flex items-center justify-between">
              <Label className="required text-sm font-normal">
                Plan Pool Size
              </Label>
              <Label className="text-xs font-normal text-green-400">
                {getFormattedValue(sharesAvailable, currency)} shares available
              </Label>
            </div>
            <Input type="number" {...formik.getFieldProps("planPoolSize")} />
            {formik.touched.planPoolSize && formik.errors.planPoolSize && (
              <Error text={formik.errors.planPoolSize} />
            )}
          </Box>
        </HStack>
        <Box className="w-1/2 pr-3">
          <Label className="required">Note </Label>
          <Input type="textarea" {...formik.getFieldProps("note")} />
          {formik.touched.note && formik.errors.note && (
            <Error text={formik.errors.note} />
          )}
        </Box>
        {!areAnyPlanDocumentsAvailable &&
          formik.values.esopPlanState.toUpperCase() === PlanState.ACTIVE && (
            <InfoBox message="There are no Plan Document(s) available to the employees in the Employee Portal. Please ensure that the necessary documents are uploaded and marked to be shown to employees in order for them to access these documents." />
          )}
        <HStack className="flex justify-end gap-4">
          <ButtonSecondary onClick={() => onClose()}>Cancel</ButtonSecondary>
          <ButtonPrimary
            className={`flex items-center self-end justify-center ${
              status === "success" ? "bg-green-500" : ""
            }`}
            onClick={() => {
              formik.handleSubmit();
            }}
            loading={status === "loading"}
          >
            {status === "idle" && "Confirm"}
            {status === "success" && (
              <Icon icon="clarity:success-standard-line" width={36} />
            )}
          </ButtonPrimary>
        </HStack>
      </VStack>
      <AlertDialog
        open={showDialog}
        error
        message={errorMessage.message}
        primaryActionText="Retry"
        secondaryActionText="Go Back"
        onPrimaryAction={() => {
          errorMessage.reset();
          onClose();
          setShowDialog(false);
        }}
        onSecondaryAction={() => {
          errorMessage.reset();
          onClose();
          setShowDialog(false);
        }}
        onClose={() => {
          errorMessage.reset();
          onClose();
          setShowDialog(false);
        }}
      />
    </>
  );
}
export default PlanStateChange;
